<template>
  <div ref="root" :style="rootStyle">
    <button
      type="button"
      :style="triggerStyles"
      aria-haspopup="true"
      :aria-expanded="open ? 'true' : 'false'"
      aria-label="Choose color"
      @click="toggle"
    ></button>
    <div v-if="open" :style="popoverStyle" role="menu">
      <button
        v-for="color in palette"
        :key="color"
        type="button"
        :style="swatchStyle(color)"
        :title="color"
        :aria-label="color"
        role="menuitemradio"
        :aria-checked="isSelected(color) ? 'true' : 'false'"
        @click="select(color)"
      ></button>
    </div>
  </div>
</template>

<script>
import { computed, defineComponent, onBeforeUnmount, ref } from 'vue';

// vue-swatches' "basic" preset (its default when no :colors is given).
const BASIC_PALETTE = [
  '#1FBC9C', '#1CA085', '#2ECC70', '#27AF60', '#3398DB', '#2980B9', '#A463BF', '#8E43AD',
  '#3D556E', '#222F3D', '#F2C511', '#F39C19', '#E84B3C', '#C0382B', '#DDE6E8', '#BDC3C8'
];

// A local, self-contained replacement for vue-swatches (Vue-2 only). Styled
// entirely with inline styles so it carries no stylesheet dependency — removing
// vue-swatches' global CSS can't leave it unstyled on any page.
export default defineComponent({
  name: 'HdSwatches',

  props: {
    // `modelValue`/`update:modelValue` is what `v-model` uses; `value`/`input` is
    // kept because a number of call sites bind those explicitly rather than
    // through v-model. Both are live Vue 3 APIs, not a compat leftover.
    value: {
      type: String,
      default: ''
    },
    modelValue: {
      type: String,
      default: undefined
    },
    // Accepted for parity; every caller uses squares.
    shapes: {
      type: String,
      default: 'squares'
    },
    triggerStyle: {
      type: Object,
      default: null
    },
    showBorder: {
      type: [Boolean, String],
      default: false
    },
    colors: {
      type: Array,
      default: () => BASIC_PALETTE
    }
  },

  emits: ['input', 'update:modelValue'],

  setup(props, { emit }) {
    const boundValue = computed(() =>
      (props.modelValue !== undefined ? props.modelValue : props.value)
    );
    const root = ref(null);
    const open = ref(false);

    const radius = computed(() => (props.shapes === 'circles' ? '50%' : '4px'));
    const bordered = computed(() => props.showBorder === true || props.showBorder === 'true');
    const palette = computed(() => props.colors);

    const rootStyle = computed(() => ({
      position: 'relative',
      display: 'inline-block',
      width: (props.triggerStyle && props.triggerStyle.width) || 'auto'
    }));

    const triggerStyles = computed(() => ({
      width: '42px',
      height: '42px',
      padding: 0,
      cursor: 'pointer',
      borderRadius: radius.value,
      border: bordered.value ? '2px solid #adb5bd' : '1px solid #ced4da',
      backgroundColor: boundValue.value || '#ffffff',
      ...(props.triggerStyle || {})
    }));

    const popoverStyle = computed(() => ({
      position: 'absolute',
      top: 'calc(100% + 4px)',
      left: '0',
      zIndex: 1080,
      display: 'grid',
      gridTemplateColumns: 'repeat(8, 1fr)',
      gap: '4px',
      padding: '8px',
      width: 'max-content',
      maxWidth: '90vw',
      backgroundColor: '#ffffff',
      border: '1px solid #ced4da',
      borderRadius: '4px',
      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)'
    }));

    const isSelected = (color) =>
      typeof boundValue.value === 'string' && boundValue.value.toLowerCase() === color.toLowerCase();

    const swatchStyle = (color) => ({
      width: '22px',
      height: '22px',
      padding: 0,
      cursor: 'pointer',
      borderRadius: radius.value,
      border: '1px solid rgba(0, 0, 0, 0.1)',
      backgroundColor: color,
      boxShadow: isSelected(color) ? '0 0 0 2px #007bff' : 'none'
    });

    const close = () => { open.value = false; };

    const onDocumentMouseDown = (event) => {
      if (open.value && root.value && !root.value.contains(event.target)) close();
    };

    const toggle = () => {
      open.value = !open.value;
      if (open.value) {
        document.addEventListener('mousedown', onDocumentMouseDown);
      } else {
        document.removeEventListener('mousedown', onDocumentMouseDown);
      }
    };

    const select = (color) => {
      emit('input', color);
      emit('update:modelValue', color);
      close();
      document.removeEventListener('mousedown', onDocumentMouseDown);
    };

    onBeforeUnmount(() => document.removeEventListener('mousedown', onDocumentMouseDown));

    return { root, open, palette, rootStyle, triggerStyles, popoverStyle, swatchStyle, isSelected, toggle, select };
  }
});
</script>
